import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Upload, Link2, HardDrive, ClipboardPaste, FileText } from 'lucide-react';
import { overlayDismiss } from '../../../lib/overlayDismiss';

/**
 * AddSourceModal — NotebookLM 風來源新增 modal
 *
 * 接受：
 *   - 拖曳檔案到中央區
 *   - 上傳檔案：file picker，多選，支援廣泛副檔名
 *   - 網站：輸入 URL（含 YouTube）
 *   - 雲端硬碟：Phase 5 預留
 *   - 複製的文字：貼上純文字 → 當作 source
 *
 * 行為：選好後關閉 modal + 呼 onSubmit({ files?, url?, text? }) 由父層 sendMessage
 */
export function AddSourceModal({ open, onClose, onSubmit }) {
    const { t } = useTranslation('wsApi');
    const [dragOver, setDragOver] = useState(false);
    const [textMode, setTextMode] = useState(false);
    const [textContent, setTextContent] = useState('');
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (!open) {
            setTextMode(false);
            setTextContent('');
            setDragOver(false);
        }
    }, [open]);

    if (!open) return null;

    const handleFiles = (files) => {
        if (!files || files.length === 0) return;
        onSubmit({ files: Array.from(files) });
        onClose();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        handleFiles(e.dataTransfer.files);
    };

    const handleUrl = () => {
        const url = window.prompt(t('source.urlPrompt'));
        if (!url?.trim()) return;
        onSubmit({ url: url.trim() });
        onClose();
    };

    const handleTextSubmit = () => {
        if (!textContent.trim()) return;
        onSubmit({ text: textContent.trim() });
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-ink-900/30 backdrop-blur-sm z-50 grid place-items-center p-4" {...overlayDismiss(onClose)}>
            <div
                className="bg-cream-50 border border-cream-200 rounded-lg shadow-xl w-full max-w-3xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-6 py-4 border-b border-cream-200">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-400">add source</div>
                        <h2 className="heading-display text-lg font-semibold text-ink-900 mt-0.5">{t('source.title')}</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="size-8 inline-flex items-center justify-center rounded-md text-ink-400 hover:text-ink-700 hover:bg-cream-100 transition"
                    >
                        <X size={16} />
                    </button>
                </div>

                {textMode ? (
                    <div className="p-6 space-y-3">
                        <div className="font-mono text-[10px] uppercase tracking-wider text-ink-500">
                            {t('source.pasteLabel')}
                        </div>
                        <textarea
                            value={textContent}
                            onChange={(e) => setTextContent(e.target.value)}
                            rows={12}
                            placeholder={t('source.textPlaceholder')}
                            className="w-full resize-none px-3 py-2 text-sm border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white font-mono"
                            autoFocus
                        />
                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => { setTextMode(false); setTextContent(''); }}
                                className="px-4 py-2 text-sm rounded-md text-ink-700 hover:bg-cream-100 transition"
                            >
                                {t('source.back')}
                            </button>
                            <button
                                onClick={handleTextSubmit}
                                disabled={!textContent.trim()}
                                className="px-4 py-2 text-sm rounded-md bg-ink-900 text-white hover:bg-ink-700 transition disabled:bg-cream-300 disabled:cursor-not-allowed"
                            >
                                {t('source.add')}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="p-6 space-y-4">
                        {/* 拖曳區 */}
                        <div
                            onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
                            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                            className={[
                                'border-2 border-dashed rounded-xl py-14 px-6 text-center cursor-pointer transition',
                                dragOver
                                    ? 'border-accent-500 bg-accent-50'
                                    : 'border-cream-300 bg-cream-100/40 hover:bg-cream-100',
                            ].join(' ')}
                        >
                            <FileText size={32} className="mx-auto mb-3 text-ink-300" strokeWidth={1.4} />
                            <div className="heading-display text-lg text-ink-700 mb-1">
                                {dragOver ? t('source.dropActive') : t('source.dropIdle')}
                            </div>
                            <div className="text-sm text-ink-500">
                                {t('source.dropHint')}
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                onChange={(e) => handleFiles(e.target.files)}
                                accept=".pdf,.docx,.doc,.xlsx,.xls,.csv,.txt,.md,.png,.jpg,.jpeg,.gif,.webp,.pptx,.ppt,.mp3,.wav,.m4a,.mp4,.mov,.html,.htm,.json,.xml,.yaml,.yml,.rtf,.epub"
                                className="hidden"
                            />
                        </div>

                        {/* quick actions — 只留有實際支援的兩種 */}
                        <div className="grid grid-cols-2 gap-2">
                            <ActionTile icon={Upload} label={t('source.tileUpload')} onClick={() => fileInputRef.current?.click()} />
                            <ActionTile icon={ClipboardPaste} label={t('source.tilePaste')} onClick={() => setTextMode(true)} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function ActionTile({ icon: Icon, label, onClick, disabled, hint }) {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            title={hint}
            className={[
                'flex flex-col items-center gap-2 px-4 py-4 rounded-md border transition text-sm',
                disabled
                    ? 'bg-cream-50 border-cream-200 text-ink-400 cursor-not-allowed'
                    : 'bg-white border-cream-300 hover:border-accent-300 hover:bg-cream-50 text-ink-900',
            ].join(' ')}
        >
            <Icon size={18} strokeWidth={1.6} className={disabled ? 'text-ink-300' : 'text-accent-700'} />
            <span className="font-medium">{label}</span>
            {hint && !disabled && (
                <span className="font-mono text-[10px] text-ink-400 leading-none">{hint}</span>
            )}
        </button>
    );
}
